/**
 * TooltipContent - Wrapper for backward compatibility
 *
 * This component is exported from the main Tooltip.tsx for backward compatibility
 * with existing code that imports TooltipContent.
 *
 * For new code, please use the visualization-specific tooltip content components:
 * - TreemapTooltipContent
 * - OverTimeTooltipContent
 * - MarketShareTooltipContent
 * - DefaultTooltipContent
 */

import React, { memo } from "react";
import { color } from "d3";
import { useLocalization } from "@fluent/react";
import { useCurrentVizType } from "../../../routing/typedRoutes";
import { index } from "d3";
import {
  TooltipContentsContainer,
  TooltipHeader,
  TooltipBody,
} from "@atlas-int/viz/shared";
import { TradeDirection, TradeFlow, ProductClass } from "@atlas-int/data/types";
import { useExploreParams } from "../../paramSchemas";
import { useLocationsMetadata } from "@atlas-int/data";
import { useProductsMetadata } from "../../../hooks/useMetadata";
import { tooltipElementReferenceId } from "@atlas-int/viz/shared";
import { UIView, VizType } from "@atlas-int/data";
import { TreemapTooltipContent } from "./TreemapTooltipContent";
import {
  OverTimeTooltipContent,
  getOverTimeTooltipHeader,
} from "./OverTimeTooltipContent";
import {
  MarketShareTooltipContent,
  getMarketShareTooltipHeader,
} from "./MarketShareTooltipContent";
import {
  DefaultTooltipContent,
  getDefaultTooltipHeader,
} from "./DefaultTooltipContent";
import type {
  TooltipDatum,
  TooltipProductDatum,
  MarketShareDatum,
  LocationLookup,
} from "@atlas-int/viz/shared";

interface TooltipContentProps {
  datum: TooltipDatum;
  view: UIView;
  totalValue?: number;
  tradeDirection?: TradeDirection;
  productClass?: ProductClass;
  fillColor?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * Legacy TooltipContent component
 * Routes to appropriate visualization-specific tooltip based on current viz type
 */
export const TooltipContent = memo(
  React.forwardRef<HTMLDivElement, TooltipContentProps>(
    (
      { datum, view, totalValue, tradeDirection, productClass, fillColor },
      ref,
    ) => {
      const hexColor = color(fillColor)?.formatHex();
      const [
        {
          tradeFlow,
          colorBy: colorBySelection,
          layout: overTimeLayout,
          importer,
          exporter,
          product,
        },
        setQuery,
      ] = useExploreParams();

      const { l10n } = useLocalization();
      const vizType = useCurrentVizType();

      const productsState = useProductsMetadata(productClass);
      const locationsState = useLocationsMetadata();
      const productMetadata = productsState.status === "success" ? productsState.data : undefined;
      const locationMetadata = locationsState.status === "success" ? locationsState.data : undefined;
      const locationLookup = index(
        Object.values(locationMetadata ?? {}).flat(),
        (d: Record<string, unknown>) => ((d.countryId as string) ? (d.countryId as string) : (d.groupId as string)),
      ) as LocationLookup;

      let tooltipHeader;
      let tooltipBodyContents;

      // Determine header based on view and viz type
      if (vizType === VizType.OverTime) {
        tooltipHeader = getOverTimeTooltipHeader(
          datum as TooltipProductDatum,
          view,
          productClass,
          productMetadata,
        );
      } else if (vizType === VizType.MarketShare) {
        tooltipHeader = getMarketShareTooltipHeader(datum as MarketShareDatum);
      } else {
        tooltipHeader = getDefaultTooltipHeader(
          datum as TooltipProductDatum,
          view,
          productClass,
          productMetadata,
        );
      }

      // Determine body content based on viz type
      if (!datum) {
        return <></>;
      }

      if (vizType === VizType.OverTime) {
        tooltipBodyContents = (
          <OverTimeTooltipContent
            datum={datum as TooltipProductDatum}
            view={view}
            tradeDirection={tradeDirection || TradeDirection.Exports}
            tradeFlow={tradeFlow || TradeFlow.Gross}
            overTimeLayout={overTimeLayout || "value"}
            exporter={exporter || ""}
            importer={importer || ""}
            productClass={productClass}
            productMetadata={productMetadata}
          />
        );
      } else if (vizType === VizType.MarketShare) {
        tooltipBodyContents = (
          <MarketShareTooltipContent
            datum={datum as MarketShareDatum}
            view={view}
            productClass={productClass}
            productMetadata={productMetadata}
          />
        );
      } else if (vizType === VizType.Tree) {
        tooltipBodyContents = (
          <TreemapTooltipContent
            datum={datum as TooltipProductDatum}
            totalValue={totalValue || 0}
            locationLookup={locationLookup}
            colorBySelection={colorBySelection}
            tradeFlow={tradeFlow || TradeFlow.Gross}
            tradeDirection={tradeDirection || TradeDirection.Exports}
            exporter={exporter || ""}
            importer={importer || ""}
            setQuery={setQuery}
            l10n={l10n}
            view={view}
            productMetadata={productMetadata}
            productClass={productClass || ProductClass.SITCProducts}
            product={product || ""}
          />
        );
      } else {
        tooltipBodyContents = (
          <DefaultTooltipContent
            datum={datum as TooltipProductDatum}
            view={view}
            totalValue={totalValue}
            tradeDirection={tradeDirection}
            productClass={productClass}
            fillColor={fillColor}
            colorBySelection={colorBySelection}
            tradeFlow={tradeFlow}
            product={product}
            exporter={exporter}
            importer={importer}
            setQuery={setQuery}
            l10n={l10n}
            productMetadata={productMetadata}
            locationLookup={locationLookup}
          />
        );
      }

      const handleTooltipClick = (e: React.MouseEvent<HTMLSpanElement>) => {
        e.stopPropagation();
      };

      const handleTooltipMouseMoveCapture = (
        e: React.MouseEvent<HTMLSpanElement>,
      ) => {
        e.stopPropagation();
      };

      if (datum !== undefined && tooltipBodyContents !== null) {
        return (
          <TooltipContentsContainer
            onClick={handleTooltipClick}
            onMouseMoveCapture={handleTooltipMouseMoveCapture}
            id={tooltipElementReferenceId}
            ref={ref}
          >
            <TooltipHeader $backgroundColor={hexColor}>
              {tooltipHeader}
            </TooltipHeader>
            <TooltipBody>{tooltipBodyContents}</TooltipBody>
          </TooltipContentsContainer>
        );
      } else {
        return <></>;
      }
    },
  ),
);
